import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, TextField, Typography, Alert, Link } from '@mui/material';
import { useSignupWizard } from '../../../contexts/SignupWizardContext';
import { verifyEmail, resendVerificationCode } from '../../../utils/signupApi';

const RESEND_COOLDOWN_SECONDS = 60;

interface SignupStep2VerifyEmailProps {
  onNext: () => void;
  onBack: () => void;
  /**
   * Seconds to disable "Resend Code" for when this step first mounts. The initial
   * verification email is sent before the user reaches Step 2, so we start a
   * cooldown immediately to stop a premature resend while that first (sometimes
   * slow) email is still in flight — the duplicate-email bug from GH#1145. Defaults
   * to the standard resend cooldown; pass 0 to disable (e.g. in tests).
   */
  initialCooldownSeconds?: number;
}

export const SignupStep2VerifyEmail: React.FC<SignupStep2VerifyEmailProps> = ({
  onNext,
  onBack: _onBack,
  initialCooldownSeconds = RESEND_COOLDOWN_SECONDS,
}) => {
  const { state, setEmailVerified, setError, setLoading } = useSignupWizard();

  const [verificationCode, setVerificationCode] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const startCooldown = useCallback((seconds: number = RESEND_COOLDOWN_SECONDS) => {
    // Replace any in-flight cooldown so the interval ref never leaks.
    if (cooldownTimerRef.current !== null) {
      clearInterval(cooldownTimerRef.current);
    }
    setCooldownSeconds(seconds);

    const timer = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          cooldownTimerRef.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    cooldownTimerRef.current = timer;
  }, []);

  // Start the resend cooldown on mount: the initial verification email is sent
  // before the user arrives at this step, so block an immediate resend while that
  // first (sometimes slow) email is still in flight. Without this the user
  // re-requests and receives two emails — the reported GH#1145 bug.
  useEffect(() => {
    if (initialCooldownSeconds > 0 && state.signupToken) {
      startCooldown(initialCooldownSeconds);
    }
    // Mount-only: we intentionally do not restart the cooldown on later renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup cooldown timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current !== null) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const handleVerify = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setLocalError('Please enter a valid 6-digit verification code');
      return;
    }

    if (!state.signupToken) {
      setLocalError('Session expired. Please start over.');
      return;
    }

    setLoading(true);
    setError(null);
    setLocalError(null);

    try {
      const response = await verifyEmail(state.signupToken, verificationCode);

      // Backend returns status: 'email_verified' on success, not emailVerified: true
      const isVerified = response.success &&
        (response.emailVerified || response.status === 'email_verified');

      if (isVerified) {
        setEmailVerified(true);
        onNext();
      } else {
        setLocalError(response.error || response.message || 'Verification failed');
      }
    } catch (error) {
      const message = (error as Error).message || 'An unexpected error occurred';
      setLocalError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!state.signupToken) {
      setLocalError('Session expired. Please start over.');
      return;
    }

    setResendLoading(true);
    setResendSuccess(false);
    setLocalError(null);

    try {
      const response = await resendVerificationCode(state.signupToken);

      if (response.success) {
        setResendSuccess(true);
        startCooldown();
        setTimeout(() => setResendSuccess(false), 5000);
      } else {
        setLocalError(response.error || response.message || 'Failed to resend code');
      }
    } catch (error) {
      setLocalError((error as Error).message || 'Failed to resend code');
    } finally {
      setResendLoading(false);
    }
  };

  // Expose submit handler for parent component
  React.useEffect(() => {
    (window as unknown as { __signupStep2Submit: () => Promise<void> }).__signupStep2Submit =
      handleVerify;
    return () => {
      delete (window as unknown as { __signupStep2Submit?: () => Promise<void> })
        .__signupStep2Submit;
    };
  });

  const isResendDisabled = resendLoading || cooldownSeconds > 0;
  const resendLabel = resendLoading
    ? 'Sending...'
    : cooldownSeconds > 0
      ? `Resend in ${cooldownSeconds}s`
      : 'Resend Code';

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Verify Your Email
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        We sent a 6-digit verification code to <strong>{state.email}</strong>. Please enter it
        below.
      </Typography>

      {localError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {localError}
        </Alert>
      )}

      {resendSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Verification code resent successfully
        </Alert>
      )}

      <TextField
        fullWidth
        label="Verification Code"
        value={verificationCode}
        onChange={(e) => {
          // Only allow digits
          const value = e.target.value.replace(/\D/g, '').slice(0, 6);
          setVerificationCode(value);
        }}
        margin="normal"
        required
        autoComplete="one-time-code"
        inputMode="numeric"
        placeholder="000000"
        helperText="Enter the 6-digit code from your email"
        inputProps={{
          maxLength: 6,
          pattern: '[0-9]*',
        }}
      />

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Didn&apos;t receive the code?{' '}
          <Link
            component="button"
            variant="body2"
            onClick={handleResendCode}
            disabled={isResendDisabled}
            aria-disabled={isResendDisabled}
            sx={{ cursor: isResendDisabled ? 'default' : 'pointer' }}
          >
            {resendLabel}
          </Link>
        </Typography>
      </Box>

      <Alert severity="info" sx={{ mt: 3 }}>
        The verification code expires in 15 minutes. If you don&apos;t see the email, check your spam
        folder.
      </Alert>
    </Box>
  );
};

export default SignupStep2VerifyEmail;
